import { useEffect, useRef, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { ROUTES, getAccountRoute } from '@shared/constants'
import { useAuthStore } from '../store/authStore'

type NavProps = {
    email?: string | null
    onLogout: () => Promise<void> | void
}


function isNearBottom(thresholdPx: number) {

    const scrollEl = document.documentElement
    const y = window.scrollY
    const viewport = window.innerHeight
    const height = scrollEl.scrollHeight
    return y + viewport >= height - thresholdPx

}


export default function Nav({ email, onLogout }: NavProps) {

    const [isHidden, setIsHidden] = useState(false)
    const [showAccounts, setShowAccounts] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const navigate = useNavigate()
    
    // Get accounts from global store
    const { user, accounts, setActiveAccount: storeSetActiveAccount } = useAuthStore()
    
    // Check if user is admin
    const isAdmin = user?.role === 'admin' || 
                    user?.email?.toLowerCase() === 'hi@lenkostudio.com' || 
                    user?.email?.toLowerCase() === 'shelemhg@hotmail.com' || 
                    user?.email?.includes('admin')
    
    // Get active account from store or fallback
    const activeAccount = accounts.find(acc => acc.isActive) || accounts[0] || {
        id: '0',
        username: email?.split('@')[0] || 'User',
        avatarUrl: `https://ui-avatars.com/api/?name=${email?.split('@')[0] || 'User'}&background=6366f1&color=fff`,
        isActive: true
    }
    
    // Filter accounts based on search query
    const filteredAccounts = accounts.filter(acc => 
        acc.username.toLowerCase().includes(searchQuery.toLowerCase())
    )
    
    // Show search bar when more than 4 accounts
    const showSearch = accounts.length > 4

    const lastScrollYRef = useRef(0)
    const rafRef = useRef<number | null>(null)

    useEffect(() => {

        lastScrollYRef.current = window.scrollY

        const onScroll = () => {

            if (rafRef.current != null) return

            rafRef.current = window.requestAnimationFrame(() => {

                rafRef.current = null

                const currentY = window.scrollY
                const lastY = lastScrollYRef.current
                lastScrollYRef.current = currentY

                // Always show at the very top or when user reaches the bottom.
                if (currentY <= 0 || isNearBottom(24)) {
                    setIsHidden(false)
                    return
                }

                const delta = currentY - lastY

                // Ignore tiny scroll jitter.
                if (Math.abs(delta) < 8) return

                // Hide when scrolling down, show when scrolling up.
                if (delta > 0) setIsHidden(true)
                else setIsHidden(false)
            
            })
        
        }

        window.addEventListener('scroll', onScroll, { passive: true })
        return () => {

            if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current)
            window.removeEventListener('scroll', onScroll)
        
        }
    
    }, [])

    return (
        <nav
            className={
                'fixed inset-x-0 z-50 transition-transform duration-300 ' +
                // Mobile: Bottom fixed, slide down to hide
                // Desktop: Top fixed, slide up to hide
                (isHidden 
                    ? 'translate-y-full md:-translate-y-full' 
                    : 'translate-y-0') +
                ' bottom-0 md:bottom-auto md:top-0'
            }
            aria-hidden={isHidden}
        >
            {/* Desktop: full-width top bar with subtle background */}
            <div className="md:bg-slate-950/80 md:backdrop-blur-md md:border-b md:border-slate-800/50">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-[env(safe-area-inset-bottom)] md:pb-0 md:py-3">
                    {/* Mobile: floating card, Desktop: clean inline nav */}
                    <div className="mb-3 md:mb-0 rounded-2xl md:rounded-none border border-slate-800 md:border-0 bg-slate-900/95 md:bg-transparent shadow-lg md:shadow-none backdrop-blur md:backdrop-blur-none">
                    <div className="flex items-center justify-between px-3 py-2">
                        
                        {/* LEFT SIDE - Mobile: Logout Button, Desktop: Home + Navigation */}
                        <div className="flex items-center gap-1 md:gap-3">
                            {/* Sign out button - only visible on mobile */}
                            <button
                                type="button"
                                onClick={() => void onLogout()}
                                className="md:hidden inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-rose-400 transition"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path
                                        d="M10 7V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2v-2h2v2h7V5h-7v2h-2Z"
                                        fill="currentColor"
                                    />
                                    <path
                                        d="M12 8 7 12l5 4v-3h6v-2h-6V8Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <span className="hidden sm:inline">Sign out</span>
                            </button>

                            {/* Home button - only visible on desktop */}
                            <NavLink
                                to={ROUTES.DASHBOARD}
                                className={({ isActive }) =>
                                    'hidden md:inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition ' +
                                    (isActive
                                        ? 'bg-primary-500/10 text-primary-400'
                                        : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200')
                                }
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path
                                        d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <span className="hidden sm:inline">Home</span>
                            </NavLink>

                            {/* Admin links removed for security separation */}


                            {/* Security button - only visible on desktop */}
                            <NavLink
                                to={ROUTES.SECURITY}
                                className={({ isActive }) =>
                                    'hidden md:inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition ' +
                                    (isActive
                                        ? 'bg-primary-500/10 text-primary-400'
                                        : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200')
                                }
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path
                                        d="M12 2 4 5.5V11c0 5.25 3.438 9.75 8 11 4.562-1.25 8-5.75 8-11V5.5L12 2Zm0 18.01C8.613 18.86 6 15.22 6 11.08V6.78l6-2.625 6 2.625v4.3c0 4.14-2.613 7.78-6 8.93Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <span className="hidden sm:inline">Security</span>
                            </NavLink>
                        </div>

                        {/* RIGHT SIDE: Profile and Sign out (desktop only) */}
                        <div className="flex items-center gap-1 md:gap-3">
                            {/* Security on mobile only */}
                            <NavLink
                                to={ROUTES.SECURITY}
                                className={({ isActive }) =>
                                    'md:hidden inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition ' +
                                    (isActive
                                        ? 'bg-primary-500/10 text-primary-400'
                                        : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200')
                                }
                            >
                                <span className="sr-only">Security</span>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path
                                        d="M12 2 4 5.5V11c0 5.25 3.438 9.75 8 11 4.562-1.25 8-5.75 8-11V5.5L12 2Zm0 18.01C8.613 18.86 6 15.22 6 11.08V6.78l6-2.625 6 2.625v4.3c0 4.14-2.613 7.78-6 8.93Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <span className="hidden sm:inline">Security</span>
                            </NavLink>

                            {/* Admin Links - Mobile */}
                            {isAdmin && (
                                <>
                                    <NavLink
                                        to="/admin"
                                        className={({ isActive }) =>
                                            'md:hidden inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition ' +
                                            (isActive
                                                ? 'bg-sky-500/10 text-sky-400'
                                                : 'text-slate-400 hover:bg-slate-800 hover:text-sky-400')
                                        }
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                            <path
                                                d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                        <span className="hidden sm:inline">Control</span>
                                    </NavLink>
                                    <NavLink
                                        to="/database"
                                        className={({ isActive }) =>
                                            'md:hidden inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition ' +
                                            (isActive
                                                ? 'bg-sky-500/10 text-sky-400'
                                                : 'text-slate-400 hover:bg-slate-800 hover:text-sky-400')
                                        }
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                            <path 
                                                d="M12 3c-4.418 0-8 1.79-8 4v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7c0-2.21-3.582-4-8-4zm0 2c3.59 0 6.666 1.206 6.936 2.84L18.99 8.006C18.822 9.49 15.772 10.61 12 10.61c-3.772 0-6.822-1.12-6.99-2.604L4.064 7.84C4.334 6.206 7.41 5 12 5z" 
                                                fill="currentColor"
                                            />
                                        </svg>
                                        <span className="hidden sm:inline">DB</span>
                                    </NavLink>
                                </>
                            )}

                            {/* Account Selector */}
                            <div className="relative">
                                <button 
                                    onClick={() => { setShowAccounts(!showAccounts); setSearchQuery(''); }}
                                    className="flex items-center gap-2 rounded-full border-2 border-slate-700 hover:border-slate-500 transition p-0.5"
                                >
                                    <img 
                                        src={activeAccount.avatarUrl} 
                                        alt={activeAccount.username} 
                                        className="w-8 h-8 rounded-full bg-slate-800 object-cover"
                                    />
                                </button>
                                
                                {showAccounts && (
                                    <>
                                        <div className="fixed inset-0 z-10" onClick={() => setShowAccounts(false)}></div>
                                        <div className="absolute right-0 w-64 bg-slate-900 border border-slate-800 rounded-xl shadow-xl overflow-hidden z-20
                                            bottom-full mb-3 md:bottom-auto md:top-full md:mt-3 transform origin-bottom-right md:origin-top-right">
                                            <div className="px-4 py-2 border-b border-slate-800 bg-slate-950/30">
                                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                    {isAdmin ? 'Manage Accounts' : 'Switch Account'}
                                                </p>
                                            </div>
                                            
                                            {/* Search bar when more than 4 accounts */}
                                            {showSearch && (
                                                <div className="px-3 py-2 border-b border-slate-800">
                                                    <div className="relative">
                                                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                            type="text"
                                                            placeholder="Search accounts..."
                                                            value={searchQuery}
                                                            onChange={(e) => setSearchQuery(e.target.value)}
                                                            className="w-full pl-9 pr-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-primary-500"
                                                            autoFocus
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Account list */}
                                            <div className={`overflow-y-auto ${showSearch ? 'max-h-48' : 'max-h-64'}`}>
                                                {filteredAccounts.length === 0 ? (
                                                    <div className="px-4 py-6 text-center text-slate-500 text-sm">
                                                        No accounts found
                                                    </div>
                                                ) : (
                                                    filteredAccounts.map(acc => (
                                                        <button 
                                                            key={acc.id} 
                                                            onClick={() => { 
                                                                storeSetActiveAccount(acc.id); 
                                                                navigate(getAccountRoute(acc.username));
                                                                setShowAccounts(false); 
                                                                setSearchQuery(''); 
                                                            }} 
                                                            className={`flex items-center gap-3 w-full px-4 py-3 text-left transition ${
                                                                activeAccount.id === acc.id 
                                                                    ? 'bg-primary-500/10 text-primary-400' 
                                                                    : 'hover:bg-slate-800 text-slate-300 hover:text-white'
                                                            }`}
                                                        >
                                                            <img src={acc.avatarUrl} alt={acc.username} className="w-8 h-8 rounded-full bg-slate-800" />
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-medium text-sm truncate">{acc.username}</p>
                                                            </div>
                                                            {activeAccount.id === acc.id && (
                                                                <span className="text-primary-400">●</span>
                                                            )}
                                                        </button>
                                                    ))
                                                )}
                                            </div>
                                            
                                            {/* Add Account button - only for non-admins or always show */}
                                            <div className="p-2 border-t border-slate-800">
                                                 <button className="w-full py-2 text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition border border-dashed border-slate-700 hover:border-slate-600">
                                                    + Add Account
                                                 </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Sign out button - only visible on desktop */}
                            <button
                                type="button"
                                onClick={() => void onLogout()}
                                className="hidden md:inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-rose-400 transition"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path
                                        d="M10 7V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2v-2h2v2h7V5h-7v2h-2Z"
                                        fill="currentColor"
                                    />
                                    <path
                                        d="M12 8 7 12l5 4v-3h6v-2h-6V8Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <span className="hidden sm:inline">Sign out</span>
                            </button>
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </nav>
    )

}
